import { Theme, useTheme } from "@/components/theme/themeProvider"
import { sonnerToast, appToast as toast } from "@/components/ui/baseUi/sonner"
import { ChatToast } from "@/modules/chat/components/chatToast"
import { useChatToken } from "@/modules/chat/hooks/useChatToken"
import { useChatClient, useChatStoreActions, useIsUserConnected } from "@/modules/chat/stores/useChatStore"
import { Path } from "@/modules/navigation/routes"
import { useUser } from "@/modules/users/hooks/useUser"
import { ChatPageProvider } from "@modules/chat/contexts/chatPageContext"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { LoggerType } from "@modules/utils/loggerUtils"
import { type PropsWithChildren, useEffect, useEffectEvent } from "react"
import { useNavigate } from "react-router-dom"

import { type Event, type TranslationLanguages } from "stream-chat"
import { Chat, Streami18n } from "stream-chat-react"

export const ChatAppWrapper = ({ children }: PropsWithChildren) => {
  const languageCode = useLanguage() as TranslationLanguages
  const { theme } = useTheme()
  const user = useUser()
  const { data: chatToken } = useChatToken()
  const chatClient = useChatClient()
  const isUserConnected = useIsUserConnected()
  const navigate = useNavigate()
  const { setIsUserConnected } = useChatStoreActions()

  const initChatClient = useEffectEvent(async () => {
    await chatClient
      .connectUser({ id: user.id }, chatToken)
      .then(() => {
        setIsUserConnected(true)
      })
      .catch((error: unknown) => {
        console.error(LoggerType.Chat + "ChatAppWrapper -- connectUser error", error)
        setIsUserConnected(false)
      })

    function handleNewMessage(event: Event) {
      if (
        (event.type === "notification.message_new" || event.type === "message.new") &&
        event.message?.text &&
        event.message.user?.name &&
        !location.pathname.includes(Path.Chat.Root) &&
        event.message.user.id !== user.id
      ) {
        toast.custom((id) => (
          <ChatToast
            authorName={event.message?.user?.name ?? ""}
            image={event.message?.user?.image ?? ""}
            message={event.message?.text ?? ""}
            onClick={() => {
              sonnerToast.dismiss(id)
              void navigate(Path.Chat.Root)
            }}
          />
        ))
      }
    }
    chatClient.on("message.new", handleNewMessage)
    chatClient.on("notification.message_new", handleNewMessage)
  })
  useEffect(() => {
    if (!isUserConnected && chatToken) {
      void initChatClient()
    }
  }, [isUserConnected, chatToken])

  const streami18n = new Streami18n({
    language: languageCode,
    translationsForLanguage: {
      "Nothing yet...": word("chat.channel.preview.nothingYet"),
    },
  })

  return (
    <Chat
      client={chatClient}
      i18nInstance={streami18n}
      theme={theme === Theme.OwnerDark ? "str-chat__theme-dark" : "str-chat__theme-light"}
    >
      <ChatPageProvider>{children}</ChatPageProvider>
    </Chat>
  )
}
